import {
  Engine,
  KeyEvent,
  Keys,
  Label,
  Random,
  Scene,
  SceneActivationContext,
  vec,
} from 'excalibur';
import { DriftingDoodle } from '../actors/drifting-doodle';
import { MascotClock } from '../actors/mascot-clock';
import { PaperBackground } from '../actors/paper-background';
import { getAds } from '../monetization/ads';
import { MONETIZATION, isMonetizationActive } from '../monetization/config';
import { isAdsRemoved, shouldShowInterstitial } from '../monetization/entitlements';
import { trackEvent } from '../monetization/events';
import { hasCustomPlayerName, playerName, renameScores, savePlayerName, submitScore } from '../monetization/leaderboard';
import { sfx } from '../utils/audio';
import { DESIGN_WIDTH, SEEDS } from '../utils/constants';
import { PlacementRect, randomPointInRects } from '../utils/placement';
import { refreshSceneTheme } from '../themes/theme';
import { feedbackMessage, shareScoreCard, takeCapture, ShareOutcome } from '../utils/share';
import { DOODLE_KINDS } from '../utils/sketch';
import { GameResult, ReviveState } from '../utils/types';
import { HandLabel } from '../ui/label';
import { refreshSidePanelBest, refreshSidePanelLeaderboard } from '../ui/side-panel';
import { SketchButton } from '../ui/sketch-button';
import { openNameModal } from '../ui/name-modal';
import { VideoIcon } from '../ui/video-icon';

const GAMEOVER_DOODLE_ZONES: PlacementRect[] = [
  { left: 70, top: 36, right: 650, bottom: 170 },
  { left: 16, top: 190, right: 90, bottom: 1232 },
  { left: 630, top: 190, right: 704, bottom: 1232 },
];

export class GameOverScene extends Scene<GameResult> {
  private headline!: Label;
  private scoreLabel!: Label;
  private bestLabel!: Label;
  private statsLabel!: Label;
  private feedback!: HandLabel;
  private continueButton!: SketchButton;
  private playButton!: SketchButton;
  private shareButton!: SketchButton;
  private nameButton!: SketchButton;
  private titleButton!: SketchButton;
  private adPlaying = false;
  private time = 0;
  private feedbackTimer = 0;
  private sharing = false;
  private result: GameResult | null = null;

  onInitialize(): void {
    this.add(new PaperBackground());
    this.add(new MascotClock({ pos: vec(DESIGN_WIDTH / 2, 500), scale: 0.68 }));

    this.headline = new HandLabel({
      name: 'gameover-headline',
      text: "time's up!",
      pos: vec(DESIGN_WIDTH / 2, 226),
      size: 78,
      tone: 'ink',
    });
    this.add(this.headline);

    this.add(
      new HandLabel({
        name: 'gameover-caption',
        text: 'you scored',
        pos: vec(DESIGN_WIDTH / 2, 736),
        size: 30,
        tone: 'inkSoft',
      }),
    );

    this.scoreLabel = new HandLabel({
      name: 'gameover-score',
      text: '0',
      pos: vec(DESIGN_WIDTH / 2, 812),
      size: 104,
      tone: 'blueDeep',
    });
    this.add(this.scoreLabel);

    this.bestLabel = new HandLabel({
      name: 'gameover-best',
      text: 'best 0',
      pos: vec(DESIGN_WIDTH / 2, 892),
      size: 38,
      tone: 'yellowDeep',
    });
    this.add(this.bestLabel);

    this.statsLabel = new HandLabel({
      name: 'gameover-stats',
      text: 'hits 0   doubles 0   best combo 0',
      pos: vec(DESIGN_WIDTH / 2, 936),
      size: 28,
      tone: 'inkSoft',
      maxWidth: 620,
    });
    this.add(this.statsLabel);

    this.feedback = new HandLabel({
      name: 'gameover-feedback',
      text: '',
      pos: vec(DESIGN_WIDTH / 2, 966),
      size: 28,
      tone: 'mintDeep',
    });
    this.add(this.feedback);

    this.continueButton = new SketchButton({
      name: 'continue-button',
      text: 'continue',
      width: 340,
      height: 78,
      accentTone: 'pink',
      fontSize: 36,
      onPress: this.continueRun,
    });
    this.continueButton.pos = vec(DESIGN_WIDTH / 2, 1026);
    const videoIcon = new VideoIcon({ name: 'continue-video-icon' });
    videoIcon.pos = vec(-126, 2);
    this.continueButton.addChild(videoIcon);
    this.add(this.continueButton);

    this.playButton = new SketchButton({
      name: 'play-again-button',
      text: 'play again',
      width: 300,
      height: 70,
      accentTone: 'mint',
      fontSize: 36,
      onPress: this.playAgain,
    });
    this.playButton.pos = vec(DESIGN_WIDTH / 2, 1112);
    this.add(this.playButton);

    this.shareButton = new SketchButton({
      name: 'share-score-button',
      text: 'share score',
      width: 180,
      height: 56,
      accentTone: 'blue',
      fontSize: 26,
      onPress: this.shareScore,
    });
    this.shareButton.pos = vec(170, 1212);
    this.add(this.shareButton);

    this.nameButton = new SketchButton({
      name: 'name-button',
      text: 'name',
      width: 140,
      height: 56,
      accentTone: 'pink',
      fontSize: 26,
      onPress: this.editName,
    });
    this.nameButton.pos = vec(360, 1212);
    this.add(this.nameButton);

    this.titleButton = new SketchButton({
      name: 'title-button',
      text: 'title screen',
      width: 180,
      height: 56,
      accentTone: 'yellow',
      fontSize: 26,
      onPress: this.goToTitle,
    });
    this.titleButton.pos = vec(550, 1212);
    this.add(this.titleButton);

    const rng = new Random(SEEDS.drifting);
    for (let i = 0; i < 8; i++) {
      this.add(
        new DriftingDoodle({
          name: `gameover-doodle-${i}`,
          kind: DOODLE_KINDS[(i + 3) % DOODLE_KINDS.length],
          pos: randomPointInRects(rng, GAMEOVER_DOODLE_ZONES),
          size: rng.floating(12, 20),
          toneIndex: i + 1,
          seed: SEEDS.drifting + i * 29,
        }),
      );
    }
  }

  onActivate(context: SceneActivationContext<GameResult>): void {
    this.time = 0;
    this.feedbackTimer = 0;
    this.sharing = false;
    this.adPlaying = false;
    this.feedback.text = '';
    refreshSceneTheme(this);
    this.result = context.data ?? null;
    if (this.result) {
      this.headline.text = this.result.reason === 'time' ? "time's up!" : 'out of hearts!';
      this.scoreLabel.text = String(this.result.score);
      this.bestLabel.text = this.result.newBest ? 'new best!' : `best ${this.result.best}`;
      this.statsLabel.text = `hits ${this.result.hits}   doubles ${this.result.doubles}   best combo ${this.result.bestCombo}`;
      refreshSidePanelBest(this.result.best);
    }
    sfx.resume();
    sfx.gameOver();
    const hasContinue = this.canContinue();
    this.continueButton.setVisible(hasContinue);
    this.playButton.pos = vec(DESIGN_WIDTH / 2, hasContinue ? 1112 : 1050);
    this.input.keyboard.on('press', this.handleKey);
    if (this.result && this.result.score > 0) {
      void this.submitResult(this.result);
    }
    void refreshSidePanelLeaderboard();
    if (this.isInterstitialDue()) {
      void this.runInterstitial();
    }
  }

  private async submitResult(result: GameResult): Promise<void> {
    const rank = await submitScore({
      name: playerName(),
      score: result.score,
      combo: result.bestCombo,
      hits: result.hits,
      doubles: result.doubles,
    });
    if (rank !== null) {
      trackEvent('score_submitted', { rank, score: result.score });
      this.feedback.text = `rank #${rank} in the world!`;
      this.feedbackTimer = 5;
      void refreshSidePanelLeaderboard();
      if (rank <= 10 && !hasCustomPlayerName()) {
        this.promptForName('you made the top scores!', 'pick a name for the leaderboard');
      }
    }
  }

  onDeactivate(): void {
    this.input.keyboard.off('press', this.handleKey);
  }

  onPreUpdate(_engine: Engine, elapsed: number): void {
    const dt = elapsed / 1000;
    this.time += dt;
    const pulse = 1 + Math.sin(this.time * 2.4) * 0.03;
    this.scoreLabel.scale = vec(pulse, pulse);
    if (this.feedbackTimer > 0) {
      this.feedbackTimer = Math.max(0, this.feedbackTimer - dt);
      if (this.feedbackTimer === 0) {
        this.feedback.text = '';
      }
    }
  }

  private isInterstitialDue(): boolean {
    return (
      isMonetizationActive() &&
      MONETIZATION.ads.interstitial &&
      MONETIZATION.adsProvider !== 'none' &&
      !isAdsRemoved() &&
      shouldShowInterstitial(MONETIZATION.ads.interstitialEveryRuns)
    );
  }

  private async runInterstitial(): Promise<void> {
    this.adPlaying = true;
    this.setButtonsVisible(false);
    try {
      await getAds().showInterstitial();
    } catch {
      this.adPlaying = false;
      this.setButtonsVisible(true);
      return;
    }
    this.adPlaying = false;
    this.setButtonsVisible(true);
  }

  private setButtonsVisible(visible: boolean): void {
    this.continueButton.setVisible(visible && this.canContinue());
    this.playButton.setVisible(visible);
    this.shareButton.setVisible(visible);
    this.nameButton.setVisible(visible);
    this.titleButton.setVisible(visible);
  }

  private editName = (): void => {
    if (this.adPlaying) {
      return;
    }
    this.promptForName('your leaderboard name', 'shown next to your scores');
  };

  private promptForName(title: string, note: string): void {
    if (this.adPlaying) {
      return;
    }
    openNameModal({
      initial: playerName(),
      title,
      note,
      onSave: (name) => {
        savePlayerName(name);
        this.feedback.text = `saved as ${name}`;
        this.feedbackTimer = 5;
        void renameScores(name).then((ok) => {
          if (ok) {
            void refreshSidePanelLeaderboard();
          }
        });
      },
    });
  }

  private playAgain = (): void => {
    if (this.adPlaying) {
      return;
    }
    sfx.resume();
    sfx.start();
    void this.engine.goToScene('game');
  };

  private goToTitle = (): void => {
    if (this.adPlaying) {
      return;
    }
    sfx.resume();
    sfx.start();
    void this.engine.goToScene('title');
  };

  private shareScore = (): void => {
    if (this.adPlaying) {
      return;
    }
    void this.runShare();
  };

  private canContinue(): boolean {
    const result = this.result;
    if (!result) {
      return false;
    }
    if (
      !isMonetizationActive() ||
      !MONETIZATION.revive.enabled ||
      !MONETIZATION.ads.rewarded ||
      MONETIZATION.adsProvider === 'none'
    ) {
      return false;
    }
    return result.revives < MONETIZATION.revive.maxPerRun;
  }

  private continueRun = (): void => {
    if (this.adPlaying) {
      return;
    }
    void this.runContinue();
  };

  private async runContinue(): Promise<void> {
    const result = this.result;
    if (!result || !this.canContinue() || this.sharing || this.adPlaying) {
      return;
    }
    this.sharing = true;
    sfx.resume();
    this.feedback.text = 'finding an ad...';
    this.feedbackTimer = 0;
    const prepared = await getAds().prepareRewarded();
    if (!prepared) {
      this.sharing = false;
      this.feedback.text = 'no ad available right now';
      this.feedbackTimer = 3;
      return;
    }
    const outcome = await getAds().showRewarded();
    this.sharing = false;
    if (outcome === 'granted') {
      trackEvent('run_revived', { score: result.score });
      const revive: ReviveState = {
        score: result.score,
        hits: result.hits,
        doubles: result.doubles,
        bestCombo: result.bestCombo,
        revives: result.revives + 1,
      };
      void this.engine.goToScene('game', { sceneActivationData: revive });
      return;
    }
    this.feedback.text = outcome === 'dismissed' ? 'no worries!' : 'ad unavailable';
    this.feedbackTimer = 3;
  };

  private async runShare(): Promise<void> {
    const result = this.result;
    if (!result || this.sharing) {
      return;
    }
    this.sharing = true;
    sfx.resume();
    this.feedback.text = 'making a doodle...';
    let outcome: ShareOutcome = 'failed';
    try {
      outcome = await shareScoreCard(result, takeCapture());
    } catch {
      outcome = 'failed';
    }
    this.sharing = false;
    this.feedback.text = feedbackMessage(outcome);
    this.feedbackTimer = 4;
  }

  private handleKey = (evt: KeyEvent): void => {
    if (evt.key === Keys.Space || evt.key === Keys.Enter) {
      this.playAgain();
    }
  };
}
